import React, { useContext, useEffect, useRef } from "react";
import type { AgentUiNode } from "src/components/views/ai/assistant/uiContext/agentUiNodeTypes.ts";
import {
	AgentUiRegistrationEnabledContext,
	AgentUiRegistryContext,
	AgentUiScopeIdContext,
} from "src/components/views/ai/assistant/uiContext/AgentUiContext.ts";
import { warnIfLarge } from "src/components/views/ai/assistant/uiContext/agentUiContextWarnings.ts";

const NODE_WARNING_CHARACTERS = 10_000;

type AgentUiNodeSource = AgentUiNode | (() => AgentUiNode);

export interface AgentUiNodeProps extends React.PropsWithChildren {
	enabled?: boolean;
	node: AgentUiNodeSource;
	parentScopeId?: string;
}

export function AgentUiScopeNode({ children, enabled = true, node, parentScopeId }: AgentUiNodeProps) {
	const isRegistrationEnabled = useContext(AgentUiRegistrationEnabledContext);
	const nodeIdRef = useRegisterAgentUiNode(node, parentScopeId, enabled);

	return (
		<AgentUiScopeIdContext.Provider value={nodeIdRef.current}>
			<AgentUiRegistrationEnabledContext.Provider value={isRegistrationEnabled && enabled}>
				{children}
			</AgentUiRegistrationEnabledContext.Provider>
		</AgentUiScopeIdContext.Provider>
	);
}

export function AgentUiLeafNode({ children, enabled = true, node, parentScopeId }: AgentUiNodeProps) {
	useRegisterAgentUiNode(node, parentScopeId, enabled);

	return <>{children}</>;
}

function useRegisterAgentUiNode(node: AgentUiNodeSource, parentScopeId: string | undefined, enabled: boolean) {
	const registry = useContext(AgentUiRegistryContext);
	const isRegistrationEnabled = useContext(AgentUiRegistrationEnabledContext);
	const currentScopeId = useContext(AgentUiScopeIdContext);
	const nodeIdRef = useRef<string>(crypto.randomUUID());
	// Node values are read lazily from nodeRef. This flag lets the second effect notify only after the node is
	// actually present in the registry.
	const isRegisteredRef = useRef(false);
	const nodeRef = useRef(node);
	nodeRef.current = node;

	useEffect(() => {
		if (registry == null || !isRegistrationEnabled || !enabled) return;
		isRegisteredRef.current = true;
		const unregister = registry.registerNode({
			id: nodeIdRef.current,
			parentId: parentScopeId ?? currentScopeId,
			getNode: () => {
				const currentNode = resolveNode(nodeRef.current);
				warnIfLarge("Agent UI node", currentNode, NODE_WARNING_CHARACTERS, {
					kind: currentNode.kind,
					key: currentNode.key ?? null,
				});
				return currentNode;
			},
		});
		return () => {
			isRegisteredRef.current = false;
			unregister();
		};
	}, [currentScopeId, enabled, isRegistrationEnabled, parentScopeId, registry]);

	// Registration mount/unmount changes are handled above. This effect covers changes to the already-registered
	// node data, such as data grid selection changes.
	useEffect(() => {
		if (registry == null || !isRegistrationEnabled || !enabled || !isRegisteredRef.current) return;
		registry.notifyNodeChanged();
	}, [enabled, isRegistrationEnabled, node, registry]);

	return nodeIdRef;
}

function resolveNode(node: AgentUiNodeSource): AgentUiNode {
	return typeof node === "function" ? node() : node;
}
